"use client";
import Image from "next/image";
import Link from "next/link";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { Button, buttonVariants } from "@heroui/react";
import { useEffect, useState } from "react";
import { SCHEDULE_DEMO_URL } from "@/lib/site-config";

const SCROLL_ENTER = 16;
const SCROLL_LEAVE = 4;

// Sign in on the app origin directly so the Clerk session is established on
// app.getverify.cl (no www → app session carry / CORS handshake hop).
const APP_URL = process.env.NEXT_PUBLIC_APP_URL || "https://app.getverify.cl";
const APP_SIGN_IN_URL = APP_URL + "/sign-in";

// Local dev collapses www + app onto one localhost origin (see proxy.ts), so
// there the sign-in page is a same-origin route we can prefetch and reach with
// a soft navigation. Decided from the inlined env var, so server and client
// render the same element (no hydration mismatch).
const SIGN_IN_IS_SAME_ORIGIN = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/?$/.test(APP_URL);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();

  useEffect(() => {
    setScrolled(window.scrollY > SCROLL_ENTER);
  }, []);

  useMotionValueEvent(scrollY, "change", (latest) => {
    setScrolled((prev) => {
      if (!prev && latest > SCROLL_ENTER) return true;
      if (prev && latest < SCROLL_LEAVE) return false;
      return prev;
    });
  });

  return (
    <div className="pointer-events-none sticky top-0 z-50 flex w-full justify-center pt-2.5">
      <motion.header
        initial={false}
        animate={{
          maxWidth: scrolled ? "720px" : "975px",
          marginLeft: scrolled ? 12 : 0,
          marginRight: scrolled ? 12 : 0,
          backgroundColor: scrolled
            ? "rgba(255, 255, 255, 0.64)"
            : "rgba(255, 255, 255, 0)",
          borderTopColor: scrolled
            ? "rgba(21, 41, 47, 0.08)"
            : "rgba(21, 41, 47, 0)",
          borderRightColor: scrolled
            ? "rgba(21, 41, 47, 0.08)"
            : "rgba(21, 41, 47, 0)",
          borderLeftColor: scrolled
            ? "rgba(21, 41, 47, 0.08)"
            : "rgba(21, 41, 47, 0)",
          borderBottomColor: scrolled
            ? "rgba(21, 41, 47, 0.08)"
            : "rgba(21, 41, 47, 0)",
          borderRadius: scrolled ? 9999 : 0,
          backdropFilter: scrolled
            ? "blur(16px) saturate(180%)"
            : "blur(0px) saturate(100%)"
        }}
        transition={{
          duration: 0.5,
          ease: [0.32, 0.72, 0, 1],
        }}
        style={{
          WebkitBackdropFilter: scrolled
            ? "blur(16px) saturate(180%)"
            : "blur(0px) saturate(100%)",
        }}
        className="pointer-events-auto w-full border h-fit"
      >
        <nav className="flex w-full items-center justify-between px-4 py-2 sm:px-6 lg:px-3">
          <Link href="/" aria-label="Verify" className="ml-1 flex shrink-0 items-center sm:ml-2">
            <Image
              src="/brand/logo-gradient.svg"
              alt="Verify"
              width={1131}
              height={311}
              priority
              className="block h-auto w-[84px] sm:w-[90px]"
              sizes="(min-width: 640px) 100px, 90px"
            />
          </Link>

          {/* Right: Actions */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* A real link (styled as the ghost Button it replaced) so it can be
                prefetched / opened in a new tab. Pressed state: a crisp 0.98
                scale over 120 ms; colours keep their 300 ms scroll morph. */}
            {SIGN_IN_IS_SAME_ORIGIN ? (
              <Link
                href="/sign-in"
                prefetch
                className={signInClassName(scrolled)}
              >
                Iniciar sesión
              </Link>
            ) : (
              <a href={APP_SIGN_IN_URL} className={signInClassName(scrolled)}>
                Iniciar sesión
              </a>
            )}
            {/* Demo CTA appears only once the hero CTA has scrolled away */}
            <motion.div
              initial={false}
              animate={{
                width: scrolled ? "auto" : 0,
                opacity: scrolled ? 1 : 0,
                scale: scrolled ? 1 : 0.9,
              }}
              transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
              className="overflow-hidden"
              style={{ pointerEvents: scrolled ? "auto" : "none" }}
              aria-hidden={!scrolled}
            >
              <Button
                onPress={() => {
                  window.open(SCHEDULE_DEMO_URL, "_blank", "noopener,noreferrer");
                }}
                excludeFromTabOrder={!scrolled}
                className="whitespace-nowrap rounded-full bg-accent px-3 text-[13px] sm:px-4 font-semibold text-white transition-colors hover:bg-[#5570e0]"
              >
                Agendar demo
              </Button>
            </motion.div>
          </div>
        </nav>
      </motion.header>
    </div>
  );
}

function signInClassName(scrolled: boolean) {
  return `${buttonVariants({ variant: "ghost" })} rounded-full px-3 text-[13px] font-medium sm:px-4 [transition:background-color_300ms_ease,border-color_300ms_ease,color_300ms_ease,transform_120ms_cubic-bezier(0.22,1,0.36,1)] active:[transform:scale(0.98)] focus-visible:status-focused ${
    scrolled
      ? "bg-white text-black border border-gray-200"
      : "text-dark-teal/80"
  }`;
}
